import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, CircularProgress, Alert,
  Stack, Autocomplete, Box, Typography,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, Campaign as CampaignIcon } from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

import { PUBLICATION_STATUSES, PUBLICATION_TYPES } from '../publication/publicationConstants';
import JournalPicker from '../publication/JournalPicker';
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'tr', label: 'Turkish' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'pl', label: 'Polish' },
  { value: 'ar', label: 'Arabic' },
  { value: 'zh', label: 'Chinese (Simplified)' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'ru', label: 'Russian' },
];

// These mirror FundingCallCollaboration.STATUS_CHOICES on the backend
const STATUS_OPTIONS = [
  { value: 'preparing',    label: 'Preparing Application' },
  { value: 'submitted',    label: 'Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'shortlisted',  label: 'Shortlisted' },
  { value: 'awarded',      label: 'Awarded / Funded' },
  { value: 'rejected',     label: 'Rejected' },
  { value: 'withdrawn',    label: 'Withdrawn' },
];

const EditProposalDialog = ({ open, onClose, proposal, onSuccess }) => {
  // Publications have their own lifecycle and no call / duration
  const isPublication = proposal?.document_type === 'publication';
  const statusOptions = isPublication ? PUBLICATION_STATUSES : STATUS_OPTIONS;
  const [formData, setFormData] = useState({
    acronym: '',
    title: '',
    // abstract: '',
    language: 'en',
    status: 'preparing',
    duration_months: '',
    ethics_flag: false,
    publication_type: '',
    target_journal: '',
    journal_style: null, // repository style picked for the journal (switches the reference style)
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Funding call state
  const [selectedCall, setSelectedCall] = useState(null);
  const [initialCallId, setInitialCallId] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [callSearchQuery, setCallSearchQuery] = useState('');

  useEffect(() => {
    if (proposal) {
      setFormData({
        acronym:         proposal.acronym          || '',
        title:           proposal.title            || '',
        // abstract:        proposal.abstract         || '',
        language:        proposal.language         || 'en',
        status:          proposal.status           || 'preparing',
        duration_months: proposal.duration_months  || '',
        ethics_flag:     proposal.ethics_flag      || false,
        publication_type: proposal.publication_type || 'journal_article',
        target_journal:   proposal.target_journal   || '',
        journal_style:    null,
      });

      // Resolve the call: prefer embedded call_details, otherwise fetch by ID
      const callId = proposal.call || null;
      setInitialCallId(callId);
      setSelectedCall(null);
      setCallSearchQuery('');
      if (callId) {
        axiosService.get(`/funding/calls/${callId}/`)
          .then(res => setSelectedCall(res.data))
          .catch(() => {});
      }
    }
  }, [proposal]);

  // Load initial call list when dialog opens
  useEffect(() => {
    if (!open) return;
    setLoadingCalls(true);
    axiosService.get('/funding/calls/', { params: { page_size: 30 } })
      .then(res => setCalls(res?.data?.results || res?.data || []))
      .catch(() => setCalls([]))
      .finally(() => setLoadingCalls(false));
  }, [open]);

  // Debounced call search — only fires when user types ≥2 chars
  useEffect(() => {
    if (callSearchQuery.length < 2) {
      if (callSearchQuery === '') {
        // Restore initial list when user clears the input
        setLoadingCalls(true);
        axiosService.get('/funding/calls/', { params: { page_size: 30 } })
          .then(res => setCalls(res?.data?.results || res?.data || []))
          .catch(() => setCalls([]))
          .finally(() => setLoadingCalls(false));
      }
      return;
    }
    const timer = setTimeout(() => {
      setLoadingCalls(true);
      axiosService.get('/funding/calls/', { params: { search: callSearchQuery, page_size: 20 } })
        .then(res => setCalls(res?.data?.results || res?.data || []))
        .catch(() => setCalls([]))
        .finally(() => setLoadingCalls(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [callSearchQuery]);

  const set = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const newCallId = selectedCall?.id || null;
      const callChanged = newCallId !== initialCallId;

      const common = {
        acronym:     formData.acronym,
        title:       formData.title,
        language:    formData.language,
        status:      formData.status,
        ethics_flag: formData.ethics_flag,
      };
      const res = await axiosService.patch(`/proposal/proposals/${proposal.id}/`, isPublication
        ? {
            ...common,
            publication_type: formData.publication_type,
            target_journal: formData.target_journal,
            ...(formData.journal_style && {
              citation_style: formData.journal_style.id,
              citation_style_title: formData.journal_style.title,
            }),
          }
        : {
            ...common,
            duration_months: formData.duration_months ? parseInt(formData.duration_months) : null,
            ...(callChanged && { call: newCallId }),
          });

      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        Object.values(err.response?.data || {}).flat().join(', ') ||
        'Failed to update proposal. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isPublication ? 'Edit Publication Details' : 'Edit Proposal Details'}</DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Row 1: Acronym + Status */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Acronym" required fullWidth
              value={formData.acronym}
              onChange={set('acronym')}
              disabled={saving}
            />

{/* Row 5: Duration */}
          {!isPublication && (
          <TextField
            label="Duration (months)" type="number" fullWidth
            value={formData.duration_months}
            onChange={set('duration_months')}
            disabled={saving}
            slotProps={{ htmlInput: { min: 0 } }}
          />
          )}

            <TextField
              label="Status" select fullWidth
              value={formData.status}
              onChange={set('status')}
              disabled={saving}
            >
              {statusOptions.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>


          </Stack>

          {/* Row 2: Title */}
          <TextField
            label="Title" required fullWidth
            value={formData.title}
            onChange={set('title')}
            disabled={saving}
          />

          {/* Row 3: Abstract */}
          {/* <TextField
            label="Abstract" multiline rows={4} fullWidth
            value={formData.abstract}
            onChange={set('abstract')}
            disabled={saving}
          /> */}



{isPublication && (
          <Stack direction="row" spacing={2}>
            <TextField
              label="Publication Type" select sx={{ flex: 1, minWidth: 0 }}
              value={formData.publication_type}
              onChange={set('publication_type')}
              disabled={saving}
            >
              {PUBLICATION_TYPES.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1, minWidth: 0 }}>
            <JournalPicker
              label="Target Journal"
              value={{ title: formData.target_journal, styleId: formData.journal_style?.id || null }}
              onChange={(j) => setFormData(prev => ({
                ...prev,
                target_journal: j.title,
                journal_style: j.styleId ? { id: j.styleId, title: j.styleTitle } : null,
              }))}
              helperText={formData.journal_style ? 'References will switch to this journal’s style' : 'Pick from the list to use the journal’s reference style'}
            />
            </Box>
          </Stack>
)}

{/* Row 6: Related Funding Call */}
          {!isPublication && (
          <Autocomplete
            value={selectedCall}
            onChange={(_, newValue) => setSelectedCall(newValue)}
            onInputChange={(_, newInput, reason) => {
              // Only update search query when the user actually types
              if (reason === 'input') setCallSearchQuery(newInput);
              else if (reason === 'clear') setCallSearchQuery('');
            }}
            options={calls}
            getOptionLabel={(option) => option?.title || option?.code || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            filterOptions={(x) => x}
            loading={loadingCalls}
            disabled={saving}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Related Funding Call"
                placeholder="Search for funding calls…"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <CampaignIcon sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loadingCalls && <CircularProgress color="inherit" size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{option.code || 'No code'}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.title}</Typography>
                </Box>
              </li>
            )}
          />
          )}

          {/* Row 4: Language + Ethics */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="AI Output Language" select fullWidth
              value={formData.language}
              onChange={set('language')}
              disabled={saving}
              helperText="The AI writing assistant will produce text in this language"
            >
              {LANGUAGES.map(l => (
                <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Ethics Review Required" select fullWidth
              value={formData.ethics_flag}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, ethics_flag: e.target.value === true || e.target.value === 'true' }));
                setError(null);
              }}
              disabled={saving}
            >
              <MenuItem value={false}>No</MenuItem>
              <MenuItem value={true}>Yes</MenuItem>
            </TextField>
          </Stack>



 
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CancelIcon />} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={saving || !formData.acronym.trim()}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProposalDialog;
